// src/components/widgets/RecordPedidosWidget.js
// Widget "Récord de Pedidos" 🏆 — muestra la foto congelada del mejor día de
// pedidos válidos (colección `records/pedidos_dia`, escrita por el cron
// cronVerificarRecordPedidos a las 16:10 GT) y el "camino al récord" de HOY
// (comparando contra `estadisticas_diarias/{hoy}`). Se muestra por PERMISO
// (`widget-record-pedidos`, sección WIDGETS de Gestión de Permisos).
// Costo: 2 lecturas de Firestore por apertura. Cero BigQuery.
import React, { useState, useEffect } from 'react';
import { db } from '../../services/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { TrophyIcon, ClockIcon, ChartBarIcon, BuildingOfficeIcon, BoltIcon } from '../ui/Icons';
import useEsMovil from '../../hooks/useEsMovil';
import useConteoAnimado from '../../hooks/useConteoAnimado';
import { ESTILO_TILE, BadgeTendencia, DonaEstados } from './widgetsUI';

// Estilos responsivos para móvil
const estilosResponsivos = `
  @media (max-width: 768px) {
    .record-widget-container {
      margin: 15px 0 !important;
    }

    .record-header {
      flex-direction: column !important;
      align-items: center !important;
      text-align: center;
      margin-bottom: 12px !important;
    }

    .record-header h3 {
      font-size: 18px !important;
      margin: 0 !important;
    }

    .record-header small {
      font-size: 11px !important;
    }

    .record-header-der {
      width: 100%;
      flex-direction: column !important;
      gap: 8px !important;
    }

    .record-tiles {
      grid-template-columns: 1fr 1fr !important;
      gap: 8px !important;
    }

    .record-tile {
      padding: 10px !important;
    }

    .record-viz-grid {
      grid-template-columns: 1fr !important;
      gap: 10px !important;
    }

    .viz-card-record {
      padding: 12px !important;
    }
  }
`;

// '2025-12-29' → '29/12/2025' (formato de fechas de la casa)
const fmtFechaGT = (iso) => {
  if (!iso) return '';
  const [a, m, d] = iso.split('-');
  return `${d}/${m}/${a}`;
};

const fmtHoyISO = () => {
  const hoy = new Date();
  return `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}-${String(hoy.getDate()).padStart(2, '0')}`;
};

const formatearMoneda = (monto) => {
  return 'Q ' + parseFloat(monto).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

// Par de cards de composición (dona + Capital vs Forza) de un día récord.
// Son 2 récords distintos (pedidos y ventas): si algún día caen en fechas
// diferentes, el widget muestra una composición por cada uno.
function ComposicionRecord({ rec, etiquetaDia, barrasListas, isMobile }) {
  if (!rec) return null;
  const montoCapital = rec.desgloseCapitalForza?.Capital?.monto || 0;
  const montoForza = rec.desgloseCapitalForza?.FORZA?.monto || 0;
  const totalCapitalForza = montoCapital + montoForza;
  const pctCapital = totalCapitalForza > 0 ? (montoCapital / totalCapitalForza) * 100 : 0;

  return (
    <div className="record-viz-grid stagger-list" style={{
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))',
      gap: isMobile ? '10px' : '14px',
      marginBottom: isMobile ? '10px' : '14px'
    }}>
      {/* Dona: composición por estado */}
      <div className="card viz-card-record" style={{ ...ESTILO_TILE, display: 'flex', flexDirection: 'column' }}>
        <div style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--color-texto-secundario)', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '5px' }}>
          <span style={{ display: 'inline-flex' }}><ChartBarIcon className="w-4 h-4" /></span> Así se componía {etiquetaDia}
        </div>
        <DonaEstados conteos={rec.estadisticasEstados} total={rec.totalPedidos} />
        {rec.reconstruido && (
          <div style={{ marginTop: '8px', fontSize: '10px', color: 'var(--color-texto-secundario)', lineHeight: 1.5 }}>
            Los estados reflejan cómo están hoy esos pedidos (ese día estaban recién enviados).
          </div>
        )}
      </div>

      {/* Capital vs Forza del día récord */}
      <div className="card viz-card-record" style={{ ...ESTILO_TILE, display: 'flex', flexDirection: 'column' }}>
        <div style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--color-texto-secundario)', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '5px' }}>
          <span style={{ display: 'inline-flex' }}><BuildingOfficeIcon className="w-4 h-4" /></span> Capital vs Forza {etiquetaDia}
        </div>
        {totalCapitalForza > 0 && (
          <div style={{
            display: 'flex',
            height: '10px',
            borderRadius: 'var(--radio-sm)',
            overflow: 'hidden',
            backgroundColor: 'var(--color-borde)',
            marginBottom: '10px'
          }}>
            <div style={{
              width: barrasListas ? `${pctCapital}%` : '0%',
              backgroundColor: 'var(--color-info)',
              transition: 'width 0.8s ease'
            }} />
            <div style={{
              width: barrasListas ? `${100 - pctCapital}%` : '0%',
              backgroundColor: 'var(--color-naranja)',
              transition: 'width 0.8s ease'
            }} />
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', fontSize: '12px' }}>
          <span style={{ color: 'var(--color-info-texto)', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <BuildingOfficeIcon className="w-4 h-4" /> CAPITAL · {rec.desgloseCapitalForza?.Capital?.cantidad || 0}
          </span>
          <strong style={{ color: 'var(--color-info-texto)', fontVariantNumeric: 'tabular-nums' }}>{formatearMoneda(montoCapital)}</strong>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', fontSize: '12px' }}>
          <span style={{ color: 'var(--color-naranja-texto)', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <BoltIcon className="w-4 h-4" /> FORZA · {rec.desgloseCapitalForza?.FORZA?.cantidad || 0}
          </span>
          <strong style={{ color: 'var(--color-naranja-texto)', fontVariantNumeric: 'tabular-nums' }}>{formatearMoneda(montoForza)}</strong>
        </div>
        <div style={{ borderTop: '1px solid var(--color-borde-suave)', marginTop: '6px', paddingTop: '8px', fontSize: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
            <span style={{ color: 'var(--color-texto-secundario)' }}>Envíos Capital</span>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatearMoneda(rec.enviosCapitalForza?.Capital || 0)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
            <span style={{ color: 'var(--color-texto-secundario)' }}>Envíos Forza</span>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatearMoneda(rec.enviosCapitalForza?.FORZA || 0)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function RecordPedidosWidget() {
  const [record, setRecord] = useState(null);
  const [recordVentas, setRecordVentas] = useState(null);
  const [hoyStats, setHoyStats] = useState(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const [recSnap, recVenSnap, hoySnap] = await Promise.all([
          getDoc(doc(db, 'records', 'pedidos_dia')),
          getDoc(doc(db, 'records', 'ventas_dia')),
          getDoc(doc(db, 'estadisticas_diarias', fmtHoyISO()))
        ]);
        if (!vivo) return;
        setRecord(recSnap.exists() ? recSnap.data() : null);
        setRecordVentas(recVenSnap.exists() ? recVenSnap.data() : null);
        setHoyStats(hoySnap.exists() ? hoySnap.data() : null);
      } catch (e) {
        console.error('Error cargando los récords:', e);
      } finally {
        if (vivo) setCargando(false);
      }
    })();
    return () => { vivo = false; };
  }, []);

  const isMobile = useEsMovil();

  // Conteos animados (los hooks van antes de cualquier return)
  const recordValidos = record?.pedidosValidos || 0;
  const recordVentasQ = recordVentas?.totalVentas || 0;
  const hoyValidos = hoyStats?.pedidosValidos || 0;
  const hoyVentasQ = hoyStats?.totalVentas || 0;
  const recordAnimado = useConteoAnimado(recordValidos);
  const recordVentasAnimado = useConteoAnimado(recordVentasQ);
  const hoyAnimado = useConteoAnimado(hoyValidos);
  const hoyVentasAnimado = useConteoAnimado(hoyVentasQ);

  // Barras que se llenan al aparecer los datos
  const [barrasListas, setBarrasListas] = useState(false);
  useEffect(() => {
    if (!record) return;
    setBarrasListas(false);
    const t = setTimeout(() => setBarrasListas(true), 60);
    return () => clearTimeout(t);
  }, [record, hoyStats]);

  // Camino a los récords (hoy) — son 2 récords independientes
  const pctHoy = recordValidos > 0 ? (hoyValidos / recordValidos) * 100 : 0;
  const pctHoyVentas = recordVentasQ > 0 ? (hoyVentasQ / recordVentasQ) * 100 : 0;
  const tendenciaHoy = recordValidos > 0 && hoyStats ? ((hoyValidos - recordValidos) / recordValidos) * 100 : null;
  const tendenciaHoyVentas = recordVentasQ > 0 && hoyStats ? ((hoyVentasQ - recordVentasQ) / recordVentasQ) * 100 : null;
  const superado = recordValidos > 0 && hoyValidos > recordValidos;
  const superadoVentas = recordVentasQ > 0 && hoyVentasQ > recordVentasQ;
  const faltanParaRecord = Math.max(0, recordValidos - hoyValidos);
  const faltanVentas = Math.max(0, recordVentasQ - hoyVentasQ);
  const mismaFecha = !!(record && recordVentas && record.fecha === recordVentas.fecha);

  if (cargando) {
    // Skeleton con la silueta del widget
    return (
      <>
        <style>{estilosResponsivos}</style>
        <div className="record-widget-container" style={{ maxWidth: '1200px', margin: isMobile ? '15px 0' : '30px auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: isMobile ? '12px' : '16px' }}>
            <div className="skeleton" style={{ width: '220px', height: '24px', borderRadius: 'var(--radio-sm)' }} />
            <div className="skeleton" style={{ width: '200px', height: '16px', borderRadius: 'var(--radio-sm)' }} />
          </div>
          <div className="record-tiles" style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, minmax(0, 1fr))', gap: isMobile ? '8px' : '14px', marginBottom: '14px' }}>
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="skeleton" style={{ height: '84px', borderRadius: 'var(--radio-lg)' }} />
            ))}
          </div>
          <div className="skeleton" style={{ width: '100%', height: '70px', borderRadius: 'var(--radio-lg)', marginBottom: '14px' }} />
          <div className="record-viz-grid" style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: isMobile ? '10px' : '14px' }}>
            {[0, 1].map((i) => (
              <div key={i} className="skeleton" style={{ height: '160px', borderRadius: 'var(--radio-lg)' }} />
            ))}
          </div>
        </div>
      </>
    );
  }

  // Todavía no hay récord registrado (el cron corre a las 4:10 pm)
  if (!record) return null;

  return (
    <>
      <style>{estilosResponsivos}</style>
      <div className="record-widget-container" style={{ maxWidth: '1200px', margin: isMobile ? '15px 0' : '30px auto' }}>
        {/* Header estilo dashboard: título a la izquierda, metadata a la
            derecha (en móvil el CSS lo apila y centra) */}
        <div className="record-header" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '10px',
          marginBottom: isMobile ? '12px' : '16px'
        }}>
          <h3 style={{
            margin: 0,
            fontSize: isMobile ? '18px' : '22px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <span style={{ color: 'var(--color-primario-fuerte)', display: 'inline-flex' }}><TrophyIcon className="w-6 h-6" /></span>
            Récord de Pedidos
          </h3>
          <div className="record-header-der" style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <small
              title={record.reconstruido
                ? (record.notaReconstruccion || 'Récord histórico reconstruido desde los datos')
                : 'Foto congelada por el sistema a las 4:10 pm del día récord'}
              style={{ color: 'var(--color-texto-secundario)', fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '5px' }}
            >
              <span style={{ display: 'inline-flex' }}><ClockIcon className="w-3 h-3" /></span>
              {mismaFecha || !recordVentas
                ? `Récords del ${fmtFechaGT(record.fecha)}`
                : `Pedidos: ${fmtFechaGT(record.fecha)} · Ventas: ${fmtFechaGT(recordVentas.fecha)}`}
              {record.reconstruido ? ' · reconstruido' : ''}
            </small>
          </div>
        </div>

        {/* Fila de KPI tiles — 2 récords distintos + el hoy contra cada uno */}
        <div className="record-tiles stagger-list" style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, minmax(0, 1fr))',
          gap: isMobile ? '8px' : '14px',
          marginBottom: isMobile ? '10px' : '14px'
        }}>
          <div className="card record-tile" style={ESTILO_TILE} title="La mayor cantidad de pedidos válidos logrados en un solo día">
            <div style={{ fontSize: '12px', color: 'var(--color-texto-secundario)', marginBottom: '4px' }}>Récord de pedidos</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '26px', fontWeight: 'bold', color: 'var(--color-texto)', fontVariantNumeric: 'tabular-nums' }}>
                {Math.round(recordAnimado)}
              </span>
              <span style={{ fontSize: '18px' }}>🥇</span>
            </div>
            <div style={{ fontSize: '11px', color: 'var(--color-texto-secundario)', marginTop: '2px' }}>
              pedidos válidos el {fmtFechaGT(record.fecha)}
            </div>
            {recordVentas && !mismaFecha && (
              <div style={{ fontSize: '11px', color: 'var(--color-texto-secundario)' }}>
                ese día se vendió {formatearMoneda(record.totalVentas || 0)}
              </div>
            )}
          </div>

          <div className="card record-tile" style={ESTILO_TILE} title="El día con más quetzales vendidos (pedidos válidos) — es un récord aparte del de pedidos">
            <div style={{ fontSize: '12px', color: 'var(--color-texto-secundario)', marginBottom: '4px' }}>Récord de ventas</div>
            {recordVentas ? (
              <>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '22px', fontWeight: 'bold', color: 'var(--color-exito)', fontVariantNumeric: 'tabular-nums', wordBreak: 'break-word' }}>
                    {formatearMoneda(recordVentasAnimado)}
                  </span>
                  <span style={{ fontSize: '18px' }}>🥇</span>
                </div>
                <div style={{ fontSize: '11px', color: 'var(--color-texto-secundario)', marginTop: '2px' }}>
                  vendidos el {fmtFechaGT(recordVentas.fecha)}
                </div>
                {!mismaFecha && (
                  <div style={{ fontSize: '11px', color: 'var(--color-texto-secundario)' }}>
                    ese día hubo {recordVentas.pedidosValidos || 0} pedidos
                  </div>
                )}
              </>
            ) : (
              <>
                <div style={{ fontSize: '22px', fontWeight: 'bold', color: 'var(--color-texto-secundario)' }}>—</div>
                <div style={{ fontSize: '11px', color: 'var(--color-texto-secundario)', marginTop: '2px' }}>
                  aún sin registrar
                </div>
              </>
            )}
          </div>

          <div className="card record-tile" style={ESTILO_TILE} title="Pedidos válidos de HOY comparados contra el récord de pedidos">
            <div style={{ fontSize: '12px', color: 'var(--color-texto-secundario)', marginBottom: '4px' }}>Hoy: pedidos</div>
            {hoyStats ? (
              <>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '26px', fontWeight: 'bold', color: superado ? 'var(--color-advertencia-texto)' : 'var(--color-texto)', fontVariantNumeric: 'tabular-nums' }}>
                    {Math.round(hoyAnimado)}
                  </span>
                  <BadgeTendencia
                    pct={tendenciaHoy}
                    etiqueta="vs récord"
                    textoIgual="al nivel del récord"
                    titulo="Pedidos válidos de hoy comparados contra el récord de pedidos"
                  />
                </div>
                <div style={{ fontSize: '11px', color: superado ? 'var(--color-advertencia-texto)' : 'var(--color-texto-secundario)', marginTop: '2px', fontWeight: superado ? 'bold' : 'normal' }}>
                  {superado ? '¡récord superado hoy!' : `faltan ${faltanParaRecord} para romperlo`}
                </div>
              </>
            ) : (
              <>
                <div style={{ fontSize: '26px', fontWeight: 'bold', color: 'var(--color-texto-secundario)' }}>—</div>
                <div style={{ fontSize: '11px', color: 'var(--color-texto-secundario)', marginTop: '2px' }}>
                  sin datos de hoy todavía
                </div>
              </>
            )}
          </div>

          <div className="card record-tile" style={ESTILO_TILE} title="Ventas de HOY comparadas contra el récord de ventas">
            <div style={{ fontSize: '12px', color: 'var(--color-texto-secundario)', marginBottom: '4px' }}>Hoy: ventas</div>
            {hoyStats && recordVentas ? (
              <>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '22px', fontWeight: 'bold', color: superadoVentas ? 'var(--color-advertencia-texto)' : 'var(--color-texto)', fontVariantNumeric: 'tabular-nums', wordBreak: 'break-word' }}>
                    {formatearMoneda(hoyVentasAnimado)}
                  </span>
                  <BadgeTendencia
                    pct={tendenciaHoyVentas}
                    etiqueta="vs récord"
                    textoIgual="al nivel del récord"
                    titulo="Ventas de hoy comparadas contra el récord de ventas"
                  />
                </div>
                <div style={{ fontSize: '11px', color: superadoVentas ? 'var(--color-advertencia-texto)' : 'var(--color-texto-secundario)', marginTop: '2px', fontWeight: superadoVentas ? 'bold' : 'normal' }}>
                  {superadoVentas ? '¡récord superado hoy!' : `faltan ${formatearMoneda(faltanVentas)}`}
                </div>
              </>
            ) : (
              <>
                <div style={{ fontSize: '22px', fontWeight: 'bold', color: 'var(--color-texto-secundario)' }}>—</div>
                <div style={{ fontSize: '11px', color: 'var(--color-texto-secundario)', marginTop: '2px' }}>
                  sin datos de hoy todavía
                </div>
              </>
            )}
          </div>
        </div>

        {/* Camino a los récords (hoy) — cada barra se pinta dorada al superar el suyo */}
        {hoyStats && recordValidos > 0 && (
          <div className="card viz-card-record" style={{ ...ESTILO_TILE, marginBottom: isMobile ? '10px' : '14px' }}>
            <div style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--color-texto-secundario)', marginBottom: '10px' }}>
              Camino a los récords (hoy)
            </div>

            {/* Barra de pedidos */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              flexWrap: 'wrap',
              gap: '6px',
              marginBottom: '6px',
              fontSize: '12px'
            }}>
              <span style={{ color: 'var(--color-texto-secundario)' }}>Pedidos</span>
              <span style={{ color: 'var(--color-texto-secundario)', fontVariantNumeric: 'tabular-nums' }}>
                {Math.round(hoyAnimado)} de {recordValidos} · {pctHoy.toFixed(0)}%
              </span>
            </div>
            <div style={{
              width: '100%',
              height: isMobile ? '18px' : '20px',
              backgroundColor: 'var(--color-borde)',
              borderRadius: 'var(--radio-sm)',
              overflow: 'hidden',
              marginBottom: '12px'
            }}>
              <div style={{
                width: barrasListas ? `${Math.min(100, pctHoy)}%` : '0%',
                height: '100%',
                backgroundColor: superado ? 'var(--color-advertencia)' : 'var(--color-primario)',
                transition: 'width 0.8s ease'
              }} />
            </div>

            {/* Barra de ventas */}
            {recordVentasQ > 0 && (
              <>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  flexWrap: 'wrap',
                  gap: '6px',
                  marginBottom: '6px',
                  fontSize: '12px'
                }}>
                  <span style={{ color: 'var(--color-texto-secundario)' }}>Ventas</span>
                  <span style={{ color: 'var(--color-texto-secundario)', fontVariantNumeric: 'tabular-nums' }}>
                    {formatearMoneda(hoyVentasAnimado)} de {formatearMoneda(recordVentasQ)} · {pctHoyVentas.toFixed(0)}%
                  </span>
                </div>
                <div style={{
                  width: '100%',
                  height: isMobile ? '18px' : '20px',
                  backgroundColor: 'var(--color-borde)',
                  borderRadius: 'var(--radio-sm)',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: barrasListas ? `${Math.min(100, pctHoyVentas)}%` : '0%',
                    height: '100%',
                    backgroundColor: superadoVentas ? 'var(--color-advertencia)' : 'var(--color-exito)',
                    transition: 'width 0.8s ease'
                  }} />
                </div>
              </>
            )}

            {(superado || superadoVentas) && (
              <p style={{
                textAlign: 'center',
                color: 'var(--color-advertencia-texto)',
                fontWeight: 'bold',
                fontSize: isMobile ? '13px' : '14px',
                margin: '10px 0 0 0'
              }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  <span className="check-pop" style={{ fontSize: '16px' }}>🥇</span>
                  {superado && superadoVentas
                    ? '¡Hoy se están rompiendo LOS DOS récords! A las 4:10 pm quedan coronados oficialmente.'
                    : superado
                      ? '¡Hoy se está rompiendo el récord de pedidos! A las 4:10 pm queda coronado oficialmente.'
                      : '¡Hoy se está rompiendo el récord de ventas! A las 4:10 pm queda coronado oficialmente.'}
                </span>
              </p>
            )}
          </div>
        )}

        {/* Composición del/los día(s) récord — si los 2 récords caen en fechas
            distintas, se muestra la composición de cada uno */}
        {mismaFecha || !recordVentas ? (
          <ComposicionRecord
            rec={record}
            etiquetaDia={`ese día (${fmtFechaGT(record.fecha)})`}
            barrasListas={barrasListas}
            isMobile={isMobile}
          />
        ) : (
          <>
            <ComposicionRecord
              rec={record}
              etiquetaDia={`· día récord de pedidos (${fmtFechaGT(record.fecha)})`}
              barrasListas={barrasListas}
              isMobile={isMobile}
            />
            <ComposicionRecord
              rec={recordVentas}
              etiquetaDia={`· día récord de ventas (${fmtFechaGT(recordVentas.fecha)})`}
              barrasListas={barrasListas}
              isMobile={isMobile}
            />
          </>
        )}
      </div>
    </>
  );
}

export default RecordPedidosWidget;
